// Import model
import { ReferenceFieldOptions } from '../models/ReferenceFieldOptions';

// Import base class serializer
import { SelectFieldOptionsSerializer } from './SelectFieldOptionsSerializer';

// Import dependency types (models and enums used in properties)
import { Condition } from '../models/Condition';
import { FieldOptions } from '../models/FieldOptions';
import { FormAction } from '../models/FormAction';

// Import dependency serializers (for complex types)
import { ConditionSerializer } from './ConditionSerializer';
import { FieldOptionsSerializer } from './FieldOptionsSerializer';
import { FormActionSerializer } from './FormActionSerializer';

// Import derived type serializers (for polymorphic deserialization)
import { MultiReferenceFieldOptionsSerializer } from './MultiReferenceFieldOptionsSerializer';
import { RemoteFileFieldOptionsSerializer } from './RemoteFileFieldOptionsSerializer';

/**
 * Serializer for ReferenceFieldOptions - handles JSON serialization and deserialization with type validation
 */
export class ReferenceFieldOptionsSerializer {
  /**
   * Deserializes a JSON string into a ReferenceFieldOptions instance
   */
  static deserialize(json: string): ReferenceFieldOptions {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a ReferenceFieldOptions instance
   */
  static fromObject(obj: any): ReferenceFieldOptions {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to ReferenceFieldOptions');
    }

    // Handle polymorphic deserialization using discriminator
    const discriminator = obj['_t'];

    switch (discriminator) {
      case 'MultiReferenceFieldOptions':
        return MultiReferenceFieldOptionsSerializer.fromObject(obj);
      case 'RemoteFileFieldOptions':
        return RemoteFileFieldOptionsSerializer.fromObject(obj);
      default:
        // Fall through to base type deserialization
        break;
    }

    const instance: ReferenceFieldOptions = {
      t: obj['_t'] as string,
      linkUrl: obj['LinkUrl'] as string | undefined,
      linkLabel: obj['LinkLabel'] as string | undefined,
      items: obj['Items'] as string | undefined,
      allowUnknown: obj['AllowUnknown'] as boolean | undefined,
      objectType: obj['ObjectType'] as string | undefined,
      objectTypeName: obj['ObjectType|Name'] as string | undefined,
      autoComplete: obj['AutoComplete'] as boolean | undefined,
      foreignFieldName: obj['ForeignFieldName'] as string | undefined,
      criteria: obj['Criteria'] ? (obj['Criteria'] as any[]).map((item: any) =>
        ConditionSerializer.fromObject(item)
      ) : [] as any,
      joinBehavior: obj['JoinBehavior'] as string | undefined,
      actions: obj['Actions'] ? (obj['Actions'] as any[]).map((item: any) =>
        FormActionSerializer.fromObject(item)
      ) : [] as any,
      contributeUserEvents: obj['ContributeUserEvents'] as string | undefined,
      valueType: obj['ValueType'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of ReferenceFieldOptions instances
   */
  static deserializeArray(json: string): ReferenceFieldOptions[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a ReferenceFieldOptions instance to a JSON string
   */
  static serialize(instance: ReferenceFieldOptions): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a ReferenceFieldOptions instance to a plain object
   */
  static toObject(instance: ReferenceFieldOptions): any {
    return {
      '_t': instance.t,
      'LinkUrl': instance.linkUrl,
      'LinkLabel': instance.linkLabel,
      'Items': instance.items,
      'AllowUnknown': instance.allowUnknown,
      'ObjectType': instance.objectType,
      'ObjectType|Name': instance.objectTypeName,
      'AutoComplete': instance.autoComplete,
      'ForeignFieldName': instance.foreignFieldName,
      'Criteria': instance.criteria?.map((item: any) =>
        ConditionSerializer.toObject(item)
      ),
      'JoinBehavior': instance.joinBehavior,
      'Actions': instance.actions?.map((item: any) =>
        FormActionSerializer.toObject(item)
      ),
      'ContributeUserEvents': instance.contributeUserEvents,
      'ValueType': instance.valueType,
    };
  }

  /**
   * Serializes an array of ReferenceFieldOptions instances to a JSON string
   */
  static serializeArray(instances: ReferenceFieldOptions[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
